/**
 * computeArRowFormulas
 *
 * The single entry point for ArTable per-row formula evaluation, shared by the
 * grid (deriveRowFormulas), every export surface, computeAggregations, workflow
 * value sources, Signify, and the agent's table readers. Skip semantics live in
 * resolveArRowFormulaSkip: the aggregations row, `suppressFormulas` rows, and
 * bundle-header rows never receive formula results. A `null` result means "no
 * value" — non-finite results (NaN, ±Infinity, parse errors) are null by the
 * evaluator's single rule, and consumers render/persist null as absence.
 * Results hold only targets whose write location is ADDRESSABLE: a typeId with
 * an empty path segment (`''`, `'.sub'`, `'col.'`) is dropped, because the
 * `FieldPath` reconcileArRow builds from it throws and the guard would compare
 * a computed number against a value nothing can store — a rewrite loop plus a
 * permanent reader alarm. Evaluation still runs the full sequence, so in-pass
 * variable resolution and the operand fingerprint are unchanged.
 * Kit-header roll-ups (`calcOutput` / deriveKitRowCalc) are out of scope here.
 */

import { arFormulaTargetFieldSegments } from './arFormulaTargetFieldSegments';
import evaluateRowFormulas from './evaluateRowFormulas';
import { readArCellOperand } from './readArCellOperand';
import { resolveArRowFormulaSkip } from './resolveArRowFormulaSkip';
import type {
  ArRowData,
  ArRowFormulaSkipReason,
  ArRowId,
  TypeId,
} from '../System.Artifacts.types';
import type { ArFormulaEngineConfig } from './createArFormulaEngineConfig';

export type ArRowFormulaComputation =
  | { skipped: true; reason: ArRowFormulaSkipReason }
  | { skipped: false; results: Map<TypeId, number | null> };

function computeArRowFormulas(
  rowId: ArRowId,
  data: Partial<ArRowData> | undefined,
  config: ArFormulaEngineConfig,
): ArRowFormulaComputation {
  const skipReason = resolveArRowFormulaSkip(rowId, data, config);
  if (skipReason !== null) {
    return { skipped: true, reason: skipReason };
  }

  const results = evaluateRowFormulas(config.formulaSequence, (typeId: TypeId) =>
    readArCellOperand(data?.cells, typeId));
  const addressable = new Map(
    [...results].filter(([typeId]) =>
      arFormulaTargetFieldSegments(typeId).every((segment) => segment !== '')),
  );
  return { skipped: false, results: addressable };
}

export { computeArRowFormulas };
